import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import "maplibre-gl/dist/maplibre-gl.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import IOSInstallPrompt from "@/components/IOSInstallPrompt";
import NotificationPermissionBanner from "@/components/NotificationPermissionBanner";
import CountrySetupModal from "@/components/CountrySetupModal";

const CONFIGURED_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Prefer the current page origin so frontend and backend share a domain at
// runtime. This avoids cross-origin CORS rejection (Kubernetes ingress sometimes
// returns `Access-Control-Allow-Origin: *` together with `Allow-Credentials: true`,
// which browsers block). If the configured URL matches the current origin (e.g.
// the preview environment), we still use it; otherwise we silently rebase to
// the live origin so deployments to any domain just work.
function resolveBackendUrl() {
  if (typeof window === "undefined") return CONFIGURED_BACKEND_URL || "";
  const here = window.location.origin;
  if (!CONFIGURED_BACKEND_URL) return here;
  try {
    const configured = new URL(CONFIGURED_BACKEND_URL).origin;
    return configured === here ? CONFIGURED_BACKEND_URL : here;
  } catch {
    return here;
  }
}

const BACKEND_URL = resolveBackendUrl();
const API = `${BACKEND_URL}/api`;

// ── Auth Context ──
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // ── Force fresh sign-in on first TWA/PWA launch ──
    // A TWA installed from Play Store shares cookies + localStorage with Chrome
    // for the same origin. So a user already signed into taladraw.com in Chrome
    // gets silently auto-logged-in when they open the TWA.
    //
    // We cannot use a "first launch done" flag in localStorage because that
    // storage IS Chrome's storage — it persists across TWA uninstalls. Instead
    // we invert the logic: the user must have *explicitly* signed in WITHIN the
    // TWA at some point (marker `taladraw_logged_in_via_twa` is set only when
    // login succeeds while `isStandalone === true`). If that marker is absent,
    // any existing session cookie is treated as a Chrome leak and we force a
    // sign-in. Once the user logs in inside the TWA, the marker sticks and
    // subsequent app launches honor the existing session normally.
    const isStandalone =
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      (typeof document !== "undefined" && document.referrer && document.referrer.startsWith("android-app://")) ||
      window.navigator.standalone === true;
    const TWA_LOGIN_MARKER = "taladraw_logged_in_via_twa_v2";
    if (isStandalone && !localStorage.getItem(TWA_LOGIN_MARKER)) {
      (async () => {
        try {
          await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        } catch { /* ignore — we clear local state regardless */ }
        // Also wipe the v1 marker so it can't accidentally re-grant trust later.
        try { localStorage.removeItem("taladraw_logged_in_via_twa"); } catch { /* ignore */ }
        setUser(null);
        setLoading(false);
      })();
      return;
    }

    checkAuth();
  }, [checkAuth]);

  // Apply the user's app-wide theme preference (Mar 2026). Uses the HTML
  // root's `data-theme` attribute so CSS `[data-theme="light"] …` rules
  // in App.css can override the top-level surface tokens. Falls back to
  // dark for anonymous users and users without the field set.
  useEffect(() => {
    const theme = user?.app_theme_preference === "light" ? "light" : "dark";
    document.documentElement.setAttribute("data-theme", theme);
  }, [user]);

  // Whenever a user becomes authenticated while running in the TWA/PWA,
  // remember that fact so we don't force them to sign in again on the next
  // app launch. The marker is only ever written from inside a standalone
  // context, so Chrome-browser-only sign-ins never set it — exactly what we
  // need to distinguish "logged in via app" from "logged in via Chrome".
  useEffect(() => {
    if (!user) return;
    if (typeof window === "undefined") return;
    const isStandalone =
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      (typeof document !== "undefined" && document.referrer && document.referrer.startsWith("android-app://")) ||
      window.navigator.standalone === true;
    if (isStandalone) {
      try { localStorage.setItem("taladraw_logged_in_via_twa_v2", "1"); } catch { /* ignore */ }
    }
  }, [user]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    // Clear the TWA login marker so the next launch re-forces sign-in if
    // they're running in standalone mode. (No-op in browser tabs.)
    try { localStorage.removeItem("taladraw_logged_in_via_twa"); } catch { /* ignore */ }
    try { localStorage.removeItem("taladraw_logged_in_via_twa_v2"); } catch { /* ignore */ }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Lazy imports ──
// MapPage carries the MapLibre + PMTiles bundle — the heaviest lazy chunk
// in the app. `webpackPrefetch: true` tells webpack to emit a
// `<link rel="prefetch">` for this chunk in the built HTML, so browsers
// fetch it during idle time (after the initial HTML/CSS/main-JS lands
// but before the user taps into /map). By the time they click into the
// map, the chunk is usually already in the HTTP cache → near-instant nav.
// Prefetch is best-effort; skipped on data-saver / metered connections.
const MapPage = React.lazy(() => import(/* webpackPrefetch: true */ "@/pages/MapPage"));
const NearbyPage = React.lazy(() => import("@/pages/NearbyPage"));
const ProfilePage = React.lazy(() => import("@/pages/ProfilePage"));
const SubscriptionPage = React.lazy(() => import("@/pages/SubscriptionPage"));
const AdminPage = React.lazy(() => import("@/pages/AdminPage"));
const AuthCallback = React.lazy(() => import("@/pages/AuthCallback"));
const PremiumPage = React.lazy(() => import("@/pages/PremiumPage"));
const EmailAuthPage = React.lazy(() => import("@/pages/EmailAuthPage"));
const WatchedPage = React.lazy(() => import("@/pages/WatchedPage"));
const SubmitATMPage = React.lazy(() => import("@/pages/SubmitATMPage"));
const MySubmissionsPage = React.lazy(() => import("@/pages/MySubmissionsPage"));
const PrivacyPolicyPage = React.lazy(() => import("@/pages/PrivacyPolicyPage"));
const ForgotPasswordPage = React.lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = React.lazy(() => import("@/pages/ResetPasswordPage"));
const SplashPage = React.lazy(() => import("@/pages/SplashPage"));
const VerifyEmailPage = React.lazy(() => import("@/pages/VerifyEmailPage"));
const CheckInboxPage = React.lazy(() => import("@/pages/CheckInboxPage"));
const TrustedNetworkPage = React.lazy(() => import("@/pages/TrustedNetworkPage"));

function AppRouter() {
  const location = useLocation();
  const { user, loading } = useAuth();

  // ── Trusted Circles: check-nearby on app visibility change ──
  // Debounced to at most once per 5 min per session. Best-effort only —
  // failures are swallowed. Uses browser geolocation with a low-accuracy
  // fallback so users with strict location prompts still get served.
  useEffect(() => {
    if (!user) return;
    let lastCheck = 0;
    let cancelled = false;
    const MIN_INTERVAL_MS = 5 * 60 * 1000;

    const runCheck = () => {
      const now = Date.now();
      if (now - lastCheck < MIN_INTERVAL_MS) return;
      lastCheck = now;
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (cancelled) return;
          axios.post(
            `${API}/trusted/check-nearby`,
            { lat: pos.coords.latitude, lng: pos.coords.longitude },
            { withCredentials: true },
          ).catch(() => { /* silent — not critical */ });
        },
        () => { /* user declined — fine, no notifications this round */ },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 60 * 1000 },
      );
    };

    // Fire once on mount, then on every visibility→visible transition.
    runCheck();
    const handler = () => {
      if (document.visibilityState === "visible") runCheck();
    };
    document.addEventListener("visibilitychange", handler);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handler);
    };
  }, [user]);

  // ── Auto-prompt for push notification permission on first launch ──
  // On Android 13+, notification permission MUST be requested at runtime — it
  // is NEVER auto-granted on install. We fire the system dialog automatically
  // the first time a signed-in user lands on the app so they don't have to
  // dig into Profile → Enable. We also silently re-subscribe if permission
  // was previously granted but the device lost its push subscription (e.g.
  // app reinstall, browser data wipe).
  //
  // IMPORTANT: A TWA installed from Play Store shares cookies + localStorage
  // with Chrome on the same device. If we use a single flag, a user who
  // visited taladraw.com in Chrome first will never be re-prompted when they
  // install the TWA — the flag is already set. So we scope the flag by
  // launch context: "pwa" (standalone / TWA) vs "web" (browser tab). Each
  // context gets exactly one prompt, so installing the TWA always asks fresh.
  useEffect(() => {
    if (!user) return;
    if (typeof window === "undefined") return;
    if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) return;

    let cancelled = false;

    // Detect TWA / installed PWA. document.referrer = "android-app://…" is the
    // canonical TWA signal set by Chrome Custom Tabs. matchMedia covers regular
    // Add-to-Home-Screen PWAs across browsers.
    const isStandalone =
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      (typeof document !== "undefined" && document.referrer && document.referrer.startsWith("android-app://")) ||
      window.navigator.standalone === true;

    const run = async () => {
      try {
        const { subscribeToPush, isPushSubscribed } = await import("@/lib/pushNotifications");

        // Already subscribed → nothing to do.
        if (await isPushSubscribed()) return;
        if (cancelled) return;

        // Permission already granted but no subscription on this device →
        // re-subscribe silently. No dialog, no toast.
        if (Notification.permission === "granted") {
          await subscribeToPush().catch(() => { /* silent */ });
          return;
        }

        // User previously denied → never ask again (browser would block anyway).
        if (Notification.permission === "denied") return;

        // In TWA / installed PWA mode we DROP the localStorage flag check
        // entirely. Android's OS-level permission state is the source of truth
        // ("default" / "granted" / "denied") — it handles dedup natively, and
        // localStorage may persist from a previous Chrome browser session
        // which would incorrectly suppress the dialog after install.
        if (!isStandalone) {
          const flagKey = `taladraw_push_prompted_${user.id || user.email}_web`;
          if (localStorage.getItem(flagKey)) return;
          localStorage.setItem(flagKey, "1");
        }

        // Small delay so the user sees the home screen first, then the dialog.
        await new Promise((r) => setTimeout(r, 1500));
        if (cancelled) return;

        await subscribeToPush().catch(() => { /* silent — denial is fine */ });
      } catch {
        /* swallow — non-fatal */
      }
    };
    run();

    return () => { cancelled = true; };
  }, [user]);

  // Check URL fragment for session_id synchronously
  if (location.hash?.includes('session_id=')) {
    return (
      <React.Suspense fallback={<LoadingScreen />}>
        <AuthCallback />
      </React.Suspense>
    );
  }

  // Auth-exempt routes (accessible without login). `/privacy` is REQUIRED to
  // be publicly accessible by Google Play, Google OAuth, and PayPal — their
  // reviewers must be able to load it from the listing URL without an
  // account. Same applies to `/privacy-policy` (alias).
  const publicPaths = ["/email-auth", "/forgot-password", "/reset-password", "/verify-email", "/check-inbox", "/privacy", "/privacy-policy"];
  const isPublicRoute = publicPaths.includes(location.pathname);

  // Show splash for unauthenticated users on protected routes
  if (!loading && !user && !isPublicRoute) {
    return (
      <React.Suspense fallback={<LoadingScreen />}>
        <SplashPage />
      </React.Suspense>
    );
  }

  return (
    <React.Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route path="/" element={<MapPage />} />
        <Route path="/map" element={<MapPage />} />
        <Route path="/dashboard" element={<MapPage />} />
        <Route path="/nearby" element={<NearbyPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/subscription" element={<SubscriptionPage />} />
        <Route path="/premium" element={<PremiumPage />} />
        <Route path="/email-auth" element={<EmailAuthPage />} />
        <Route path="/watched" element={<WatchedPage />} />
        <Route path="/submit-atm" element={<SubmitATMPage />} />
        <Route path="/my-submissions" element={<MySubmissionsPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/check-inbox" element={<CheckInboxPage />} />
        <Route path="/trusted-network" element={<TrustedNetworkPage />} />
      </Routes>
    </React.Suspense>
  );
}

function LoadingScreen() {
  return (
    <div className="h-screen w-screen flex items-center justify-center bg-[#09090B]" data-testid="loading-screen">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-2 border-[#00FF94] border-t-transparent rounded-full animate-spin" />
        <span className="text-zinc-400 text-sm font-medium">Loading TalaDraw...</span>
      </div>
    </div>
  );
}

function App() {
  // Remove platform-injected badge
  React.useEffect(() => {
    const remove = () => {
      const el = document.getElementById('emergent-badge');
      if (el) el.remove();
    };
    remove();
    const interval = setInterval(remove, 500);
    return () => clearInterval(interval);
  }, []);

  // PWA: eagerly register the service worker on app load. This is what makes
  // TalaDraw installable on Android (Add to Home Screen) and lets the
  // Bubblewrap-generated TWA boot from a cached shell when offline. The same
  // SW also handles push notifications — `pushNotifications.js` will reuse
  // the registration if it exists rather than registering a second time.
  React.useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
    // Defer until after first paint so it never blocks initial render.
    const t = setTimeout(() => {
      navigator.serviceWorker
        .register("/sw-push.js")
        .catch((err) => {
          // Non-fatal — app still works without offline cache.
          // eslint-disable-next-line no-console
          console.warn("Service worker registration failed:", err);
        });
    }, 1000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="dark">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <NotificationPermissionBanner />
          <IOSInstallPrompt />
          <CountrySetupModal />
          <Toaster
            position="top-center"
            toastOptions={{
              className: "!bg-zinc-950 !text-zinc-50 !border-zinc-800",
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export { API, BACKEND_URL };
export default App;
